import { useAtom } from "jotai";
import { AlertTriangle, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useConfiguredRouteLimits } from "@/features/routes/hooks/useConfiguredRouteLimits";
import {
  alreadyRoutedRouteId,
  buildUnassignedBreakdown,
  hasConfiguredRouteLimits,
  isDeletedPermanentCopy,
  type UnassignedReasonGroup,
} from "@/features/routes/lib/unassignedBreakdown";
import { unassignedBuildOrdersAtom } from "@/store/buildRouteTask";

/** Maximum number of order items shown per reason group before "+ N more". */
const MAX_VISIBLE_PER_GROUP = 5;

/**
 * Dismissible alert panel rendered after auto-build when some orders could not
 * be assigned to any route. Groups dropped orders by reason code and shows an
 * actionable suggestion per group.
 *
 * Grouping and reason translation come from `unassignedBreakdown.ts`, shared with
 * the notification bell and the build toasts, so all three surfaces word a reason
 * identically and a new solver code is handled in exactly one place.
 *
 * Reads from `unassignedBuildOrdersAtom`; clears the atom on dismiss.
 * Renders nothing when the atom is empty.
 */
export function UnassignedBuildAlert() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [orders, setOrders] = useAtom(unassignedBuildOrdersAtom);
  // BUG-685: the `undetermined` advice names only limits that are configured.
  const routeLimits = useConfiguredRouteLimits();

  if (orders.length === 0) return null;

  const breakdown = buildUnassignedBreakdown(
    {
      unassigned_total: orders.length,
      unassigned_truncated: false,
      unassigned_reason_counts: orders.reduce<Record<string, number>>(
        (acc, order) => {
          acc[order.reason] = (acc[order.reason] ?? 0) + 1;
          return acc;
        },
        {},
      ),
      unassigned: orders,
    },
    t,
    { routeLimits },
  );

  function handleDismiss() {
    setOrders([]);
  }

  /** Destination for a group's contextual action, or null when it has none. */
  // BUG-545 added two codes. "shift" is fixed on the VEHICLE (lengthen the shift, or
  // use one that covers the run), so it joins the /vehicles group. "undetermined" has
  // no attributed cause; it points at /settings only when a route limit is configured.
  function actionTargetFor(group: UnassignedReasonGroup): string | null {
    switch (group.reason) {
      // BUG-677: `no_room` — every van able to serve it is full, so the lever is
      // another van. BUG-679: `window_after_shift` — the vans' shifts end before
      // the window opens. BUG-685: `day_full` — another van or an earlier shift start.
      // BUG-696: `shift_over` — a van whose shift is still running.
      case "capacity":
      case "requirements":
      case "no_vehicle":
      case "shift":
      case "no_room":
      case "window_after_shift":
      case "day_full":
      case "shift_over":
        return "/vehicles";
      // BUG-679: `window_invalid` is fixed on the order itself.
      // BUG-686: a deleted / completed / vanished pick — refresh the order list,
      // re-select, or create the order again.
      case "time_window":
      case "unroutable":
      case "window_invalid":
      case "order_archived":
      case "order_terminal":
      case "order_missing":
        return "/orders";
      case "max_duration":
      case "max_distance":
      case "max_orders":
        return "/settings";
      // BUG-685: only when the advice actually names a configured limit — with
      // none there is nothing in the settings to change.
      case "undetermined":
        return hasConfiguredRouteLimits(routeLimits) ? "/settings" : null;
      // BUG-677: `routes_quota` is the tariff's routes-per-day quota. No screen of
      // the dispatcher's raises it; the suggestion text says who can.
      // BUG-686: `already_routed` links each order to ITS route instead (per row).
      case "routes_quota":
      case "already_routed":
      default:
        // An unknown code has no advice we can trust — no action button.
        return null;
    }
  }

  function actionLabelFor(target: string): string {
    if (target === "/vehicles") return t("nav.vehicles");
    if (target === "/orders") return t("nav.orders");
    return t("nav.settings");
  }

  return (
    <div
      role="alert"
      className="rounded-xl border border-brand-warning/40 bg-brand-warning/10 px-5 py-4 shadow-sm"
    >
      {/* Header row */}
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <AlertTriangle
            size={18}
            className="mt-0.5 shrink-0 text-brand-warning"
            aria-hidden="true"
          />
          <span className="text-sm font-semibold text-gray-900">
            {t("routes.buildAlert.title", { count: orders.length })}
          </span>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t("routes.buildAlert.dismiss")}
          className="rounded p-0.5 text-gray-500 transition-colors hover:bg-brand-warning/15 hover:text-gray-700"
        >
          <X size={16} />
        </button>
      </div>

      {/* Reason groups */}
      <div className="space-y-4">
        {breakdown.groups.map((group) => {
          const visible = group.orders.slice(0, MAX_VISIBLE_PER_GROUP);
          const hiddenCount = group.orders.length - visible.length;
          const target = actionTargetFor(group);

          return (
            <div key={group.reason}>
              <p className="mb-1 text-xs font-semibold text-gray-800">
                {group.label}
              </p>

              <ul className="mb-1.5 space-y-0.5">
                {visible.map((order, index) => {
                  const routeId = alreadyRoutedRouteId(order);
                  return (
                    <li key={order.order_id} className="text-xs text-gray-700">
                      {group.orderLabels[index]}
                      {order.order_address && order.order_title && (
                        <span className="ml-1 text-gray-500">
                          — {order.order_address}
                        </span>
                      )}
                      {/* BUG-677: the measured cause for THIS order, when sent. */}
                      {group.orderDetails[index] && (
                        <span className="block text-gray-500">
                          {group.orderDetails[index]}
                        </span>
                      )}
                      {/* BUG-685: this date's copy of a permanent order is gone. */}
                      {isDeletedPermanentCopy(order) && (
                        <span className="block text-gray-500">
                          {t("routes.buildAlert.permanentCopyRemoved")}
                        </span>
                      )}
                      {/* BUG-686: the route this order already sits on. */}
                      {routeId !== null && (
                        <button
                          type="button"
                          onClick={() => void navigate(`/routes/${routeId}`)}
                          className="block font-semibold text-brand-primary underline-offset-2 hover:underline"
                        >
                          {t("routes.buildAlert.openRoute")}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>

              {hiddenCount > 0 && (
                <p className="mb-1.5 text-xs text-gray-500">
                  {t("routes.buildAlert.showMore", { count: hiddenCount })}
                </p>
              )}

              {group.suggestion && (
                <p className="text-xs italic text-gray-600">
                  {group.suggestion}
                </p>
              )}

              {/* Contextual action button */}
              {target && (
                <button
                  type="button"
                  onClick={() => void navigate(target)}
                  className="mt-2 rounded-lg border border-brand-warning/40 bg-white px-3 py-1.5 text-xs font-semibold text-gray-800 transition-colors hover:bg-brand-warning/10"
                >
                  {actionLabelFor(target)}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
